import { useId, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSearch } from '../api/hooks';
import type { SearchParams } from '../api/types';
import { ErrorPanel, Loading } from '../components/ErrorPanel';
import { Pagination } from '../components/Pagination';
import { ResultList } from '../components/ResultList';
import { SearchBox } from '../components/SearchBox';
import { kindGroups, pilots, sectors, sensitivity, typesInGroup, type KindGroup } from '../labels/labels';
import { strings } from '../strings';

const sortValues = ['updated_desc', 'created_desc', 'name'] as const;
type Sort = (typeof sortValues)[number];

/** Search state lives in the URL so results can be bookmarked, shared and navigated back to. */
export function readSearchParams(params: URLSearchParams): SearchParams & { kind?: KindGroup } {
  const kind = params.get('kind') as KindGroup | null;
  const sort = params.get('sort') as Sort | null;
  const page = Number(params.get('page'));
  return {
    q: params.get('q') ?? undefined,
    kind: kind && kind in kindGroups ? kind : undefined,
    types: kind && kind in kindGroups ? typesInGroup(kind) : undefined,
    pilot: params.get('pilot') ?? undefined,
    sector: params.get('sector') ?? undefined,
    sensitivityLevel: params.get('sensitivity') ?? undefined,
    sort: sort && sortValues.includes(sort) ? sort : 'updated_desc',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: 20,
  };
}

function Select({
  label,
  value,
  options,
  onChange,
  emptyLabel = strings.search.any,
}: {
  label: string;
  value: string;
  options: [string, string][];
  onChange: (value: string) => void;
  /** Text of the empty choice: "Any" for filters, the default order for sorting. */
  emptyLabel?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-small font-bold">
        {label}
      </label>
      <select id={id} className="field" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{emptyLabel}</option>
        {options.map(([code, text]) => (
          <option key={code} value={code}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

const entries = (map: Record<string, { label: string }>): [string, string][] =>
  Object.entries(map).map(([code, v]) => [code, v.label]);

export function Search() {
  const { tenant = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const search = readSearchParams(params);
  const result = useSearch(tenant, search);
  const [filtersOpen, setFiltersOpen] = useState(
    () => typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 1024px)').matches,
  );

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const hasFilters = ['kind', 'pilot', 'sector', 'sensitivity'].some((k) => params.has(k));

  return (
    <div className="space-y-6">
      <h1 className="m-0">{strings.search.title}</h1>
      <div className="card">
        <SearchBox key={search.q ?? ''} initial={search.q ?? ''} onSearch={(q) => update('q', q.trim())} />
      </div>

      <div className="grid gap-8 lg:grid-cols-[18rem_1fr]">
        {/* Collapsed on small screens so the results are reachable without scrolling past every filter. */}
        <details
          className="self-start"
          open={filtersOpen}
          onToggle={(e) => setFiltersOpen(e.currentTarget.open)}
        >
          <summary className="cursor-pointer font-body text-h5 font-bold text-brand-orchid">{strings.search.filters}</summary>
          <div className="mt-4 space-y-4">
          <Select label={strings.search.kind} value={search.kind ?? ''} options={entries(kindGroups)} onChange={(v) => update('kind', v)} />
          <Select label={strings.search.pilot} value={search.pilot ?? ''} options={entries(pilots)} onChange={(v) => update('pilot', v)} />
          <Select label={strings.search.sector} value={search.sector ?? ''} options={entries(sectors)} onChange={(v) => update('sector', v)} />
          <Select
            label={strings.search.sensitivity}
            value={search.sensitivityLevel ?? ''}
            options={entries(sensitivity)}
            onChange={(v) => update('sensitivity', v)}
          />
          <p className="m-0 text-small text-neutral-muted">{strings.search.datasetOnlyHint}</p>
          {hasFilters && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                const next = new URLSearchParams();
                if (search.q) next.set('q', search.q);
                setParams(next);
              }}
            >
              {strings.search.clear}
            </button>
          )}
          </div>
        </details>

        <section aria-labelledby="results-title" aria-busy={result.isFetching}>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <h2 id="results-title" className="m-0" aria-live="polite">
              {result.data ? strings.search.results(result.data.totalCount) : strings.loading}
            </h2>
            <div className="w-56">
              <Select
                label={strings.search.sort}
                value={search.sort === 'updated_desc' ? '' : (search.sort ?? '')}
                options={[
                  ['created_desc', strings.search.sortCreated],
                  ['name', strings.search.sortName],
                ]}
                onChange={(v) => update('sort', v)}
                emptyLabel={strings.search.sortUpdated}
              />
            </div>
          </div>
          {result.isPending ? (
            <Loading />
          ) : result.isError ? (
            <ErrorPanel error={result.error} onRetry={() => void result.refetch()} />
          ) : result.data.items.length === 0 ? (
            <p>{strings.search.none}</p>
          ) : (
            <>
              <ResultList tenant={tenant} items={result.data.items} />
              <Pagination
                page={result.data.page}
                totalPages={result.data.totalPages}
                onChange={(p) => update('page', String(p))}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
